import React from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';
import { NO_CLIENT } from '../../utils/taskConstants';

const NONE_OPTION = { id: NO_CLIENT, clientName: '(No client)' };

/**
 * Client combobox shared by the Tasks filter bar and the task dialog.
 * Only active clients are accepted; free text clears on blur (not freeSolo).
 *
 * Props:
 * - clients: active clients [{ id, clientName }]
 * - value: null | NO_CLIENT | clientId
 * - onChange(next): next is null | NO_CLIENT | clientId
 * - includeNoneOption: pin the red "(No client)" option at the top (filter bar)
 * - label / placeholder / size / sx: passthrough presentation
 */
const ClientAutocomplete = ({
  clients,
  value,
  onChange,
  includeNoneOption = false,
  label,
  placeholder = 'All clients',
  size = 'small',
  sx = {}
}) => {
  const options = includeNoneOption ? [NONE_OPTION, ...clients] : clients;

  const selectedOption =
    value === NO_CLIENT
      ? NONE_OPTION
      : clients.find((c) => c.id === value) || null;

  const isNone = value === NO_CLIENT;

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={(e, option) => onChange(option ? option.id : null)}
      getOptionLabel={(option) => option.clientName || ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(opts, state) => {
        const input = state.inputValue.trim().toLowerCase();
        const matches = opts.filter(
          (o) =>
            o.id === NO_CLIENT ||
            o.clientName.toLowerCase().includes(input)
        );
        // Keep "(No client)" pinned at the top regardless of the typed text
        return matches.sort((a, b) => (a.id === NO_CLIENT ? -1 : b.id === NO_CLIENT ? 1 : 0));
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={
            option.id === NO_CLIENT
              ? {
                  color: '#d32f2f',
                  fontWeight: 500,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }
              : undefined
          }
        >
          {option.clientName}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          sx={
            isNone
              ? { '& .MuiInputBase-input': { color: '#d32f2f', fontWeight: 500 } }
              : undefined
          }
        />
      )}
      size={size}
      sx={sx}
    />
  );
};

export default ClientAutocomplete;
